import {
  TRANSACTION_TYPES,
  shiftCivilDays,
  formatShortCivilDate,
  fromFirestoreStatus,
  fromFirestoreType,
  getAmountCents,
  getFinancialDate,
  isValidCivilDate,
} from '../domain/finance';

export const toMovementRecord = (occurrence) => ({
  ...occurrence,
  nome: occurrence.name || occurrence.nome || '',
  categoria: occurrence.category || occurrence.categoria || '',
  tipo: fromFirestoreType(occurrence.type) || occurrence.tipo,
  status: fromFirestoreStatus(occurrence.status) || occurrence.status,
});

export const splitMovementOccurrences = (occurrences = []) => {
  const records = occurrences.map(toMovementRecord);
  return {
    receitas: records.filter((item) => item.tipo === TRANSACTION_TYPES.INCOME),
    despesas: records.filter((item) => item.tipo === TRANSACTION_TYPES.EXPENSE),
  };
};

const sumRange = (items, startDate, endDate) => items.reduce((sum, item) => {
  const date = getFinancialDate(item);
  if (!date || date < startDate || date > endDate) return sum;
  return sum + (getAmountCents(item) || 0);
}, 0);

export const buildDateRangeSeries = (receitas, despesas, startDate, endDate) => {
  if (!isValidCivilDate(startDate) || !isValidCivilDate(endDate) || startDate > endDate) return [];
  if (shiftCivilDays(startDate, 61) && endDate > shiftCivilDays(startDate, 61)) return buildMonthlyRangeSeries(receitas, despesas, startDate, endDate);
  const dates = [];
  let cursor = startDate;
  while (cursor && cursor <= endDate && dates.length <= 366) {
    dates.push(cursor);
    cursor = shiftCivilDays(cursor, 1);
  }
  if (!dates.length || dates.at(-1) !== endDate) return [];

  const bucketCount = Math.min(6, dates.length);
  const bucketSize = Math.ceil(dates.length / bucketCount);
  const buckets = [];
  for (let index = 0; index < dates.length; index += bucketSize) {
    const bucketStart = dates[index];
    const bucketEnd = dates[Math.min(index + bucketSize - 1, dates.length - 1)];
    const startLabel = formatShortCivilDate(bucketStart);
    const endLabel = formatShortCivilDate(bucketEnd);
    buckets.push({
      label: bucketStart === bucketEnd ? startLabel : `${startLabel}–${endLabel}`,
      startDate: bucketStart,
      endDate: bucketEnd,
      rangeLabel: formatShortCivilDate(bucketStart, true) + ' — ' + formatShortCivilDate(bucketEnd, true),
      income: sumRange(receitas, bucketStart, bucketEnd),
      expense: sumRange(despesas, bucketStart, bucketEnd),
    });
  }
  return buckets;
};

const monthOrdinal = (date) => Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1;
const monthStart = (ordinal) => String(Math.floor(ordinal / 12)).padStart(4, '0') + '-' + String(ordinal % 12 + 1).padStart(2, '0') + '-01';
const monthLabel = (ordinal) => {
  const date = monthStart(ordinal);
  return formatShortCivilDate(date).replace(/^1\s+/, '') + '/' + date.slice(0, 4);
};

// At most twelve points, even for decades of persisted history.
export const buildMonthlyRangeSeries = (receitas, despesas, startDate, endDate) => {
  if (!isValidCivilDate(startDate) || !isValidCivilDate(endDate) || startDate > endDate) return [];
  const first = monthOrdinal(startDate);
  const last = monthOrdinal(endDate);
  const step = Math.max(1, Math.ceil((last - first + 1) / 12));
  const buckets = [];
  for (let month = first; month <= last; month += step) {
    const finalMonth = Math.min(last, month + step - 1);
    const bucketStart = month === first ? startDate : monthStart(month);
    const bucketEnd = finalMonth === last ? endDate : shiftCivilDays(monthStart(finalMonth + 1), -1);
    buckets.push({
      label: month === finalMonth ? monthLabel(month) : monthLabel(month) + '–' + monthLabel(finalMonth),
      startDate: bucketStart,
      endDate: bucketEnd,
      rangeLabel: formatShortCivilDate(bucketStart, true) + ' — ' + formatShortCivilDate(bucketEnd, true),
      income: sumRange(receitas, bucketStart, bucketEnd),
      expense: sumRange(despesas, bucketStart, bucketEnd),
    });
  }
  return buckets;
};

export const buildHistorySeries = (receitas, despesas) => {
  const dates = [...receitas, ...despesas].map(getFinancialDate).filter(Boolean).sort();
  return dates.length ? buildMonthlyRangeSeries(receitas, despesas, dates[0], dates.at(-1)) : [];
};
